import { useEffect, useState } from "react";
import api from "../lib/api";
import { Plus, Trash2, Shield, AlertTriangle, Flame, Beaker } from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  principal: { color: "#CCFF00", icon: Shield, label: "Principal" },
  secondaire: { color: "#00D4FF", icon: Shield, label: "Secondaire" },
  aggressif: { color: "#EF4444", icon: Flame, label: "Agressif" },
  experimental: { color: "#F59E0B", icon: Beaker, label: "Expérimental" },
};

export default function MultiComptes() {
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    label: "", handle: "", role: "principal", platforms: "instagram",
    risk_level: "safe", post_frequency: "1/jour", warmup_status: "actif",
    daily_quota_likes: 80, daily_quota_comments: 30, daily_quota_follows: 15, daily_quota_dms: 10,
    followers: 0, avg_reach: 0, avg_engagement: 0, notes: "",
  });

  const load = async () => setAccounts(await api.listAccounts());
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, platforms: form.platforms.split(",").map(s => s.trim()).filter(Boolean) };
    await api.createAccount(payload);
    toast.success("Compte ajouté");
    setShowForm(false);
    setForm({ ...form, label: "", handle: "", notes: "" });
    load();
  };

  const del = async (id) => { await api.deleteAccount(id); toast.success("Compte supprimé"); load(); };

  return (
    <div className="space-y-8 animate-fadeUp" data-testid="multi-comptes-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div className="border-l-2 border-[#CCFF00] pl-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// MULTI-COMPTES</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Gestion Multi-Comptes</h1>
          <p className="text-zinc-400 mt-2">4 rôles distincts. 4 niveaux de risque. 1 stratégie unifiée.</p>
        </div>
        <button onClick={() => setShowForm(s => !s)} data-testid="add-account-btn"
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] transition-all flex items-center gap-2 glow-volt text-sm">
          <Plus className="w-4 h-4" strokeWidth={2} /> Nouveau compte
        </button>
      </header>

      {showForm && (
        <form onSubmit={submit} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-6 space-y-4" data-testid="account-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="Label" value={form.label} onChange={v => setForm({ ...form, label: v })} required testid="input-label" />
            <Input label="Handle (@...)" value={form.handle} onChange={v => setForm({ ...form, handle: v })} required testid="input-handle" />
            <Select label="Rôle" value={form.role} onChange={v => setForm({ ...form, role: v })}
              options={[["principal","Principal"],["secondaire","Secondaire"],["aggressif","Agressif"],["experimental","Expérimental"]]} testid="input-role" />
            <Input label="Plateformes (csv)" value={form.platforms} onChange={v => setForm({ ...form, platforms: v })} />
            <Select label="Risque" value={form.risk_level} onChange={v => setForm({ ...form, risk_level: v })} options={[["safe","Safe"],["medium","Medium"],["high","High"]]} />
            <Select label="Statut warm-up" value={form.warmup_status} onChange={v => setForm({ ...form, warmup_status: v })}
              options={[["neuf","Neuf"],["warmup","Warm-up"],["actif","Actif"],["shadowban","Shadowban"],["recovery","Recovery"]]} />
            <Input label="Fréquence" value={form.post_frequency} onChange={v => setForm({ ...form, post_frequency: v })} />
            <Input label="Quotas likes/j" type="number" value={form.daily_quota_likes} onChange={v => setForm({ ...form, daily_quota_likes: +v })} />
            <Input label="Quotas comments/j" type="number" value={form.daily_quota_comments} onChange={v => setForm({ ...form, daily_quota_comments: +v })} />
            <Input label="Quotas follows/j" type="number" value={form.daily_quota_follows} onChange={v => setForm({ ...form, daily_quota_follows: +v })} />
            <Input label="Quotas DMs/j" type="number" value={form.daily_quota_dms} onChange={v => setForm({ ...form, daily_quota_dms: +v })} />
            <Input label="Followers" type="number" value={form.followers} onChange={v => setForm({ ...form, followers: +v })} />
          </div>
          <textarea placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
            className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#CCFF00] rounded-sm px-3 py-2 text-sm text-white outline-none" rows={2} />
          <div className="flex gap-2">
            <button type="submit" data-testid="submit-account" className="bg-[#CCFF00] text-black px-5 py-2 rounded-sm font-medium hover:bg-[#B3E600] text-sm">Enregistrer</button>
            <button type="button" onClick={() => setShowForm(false)} className="border border-[#1E1E24] text-zinc-400 hover:text-white px-5 py-2 rounded-sm text-sm">Annuler</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {accounts.map(a => {
          const meta = ROLE_META[a.role] || ROLE_META.principal;
          const I = meta.icon;
          return (
            <div key={a.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-6 hover:border-white/20 transition" data-testid={`account-card-${a.label}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}40` }}>
                    <I className="w-5 h-5" style={{ color: meta.color }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-display text-lg font-semibold text-white">{a.label}</div>
                    <div className="font-mono text-xs text-zinc-500">{a.handle}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm" style={{ background: `${meta.color}10`, color: meta.color, border: `1px solid ${meta.color}40` }}>
                    {meta.label}
                  </span>
                  <button onClick={() => del(a.id)} className="text-zinc-600 hover:text-red-500" data-testid={`delete-${a.label}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Stat label="Abonnés" value={a.followers.toLocaleString("fr-FR")} />
                <Stat label="Reach moy." value={a.avg_reach.toLocaleString("fr-FR")} />
                <Stat label="Engagement" value={`${a.avg_engagement}%`} />
              </div>
              <div className="border-t border-[#1E1E24] pt-3 grid grid-cols-4 gap-2 text-[11px] font-mono">
                <Quota label="Likes" value={a.daily_quota_likes} />
                <Quota label="Comm." value={a.daily_quota_comments} />
                <Quota label="Follows" value={a.daily_quota_follows} />
                <Quota label="DMs" value={a.daily_quota_dms} />
              </div>
              <div className="flex items-center justify-between mt-3 text-[11px] font-mono uppercase tracking-wider">
                <span className="text-zinc-500">Risque <span style={{ color: a.risk_level === "high" ? "#EF4444" : a.risk_level === "medium" ? "#F59E0B" : "#10B981" }}>● {a.risk_level}</span></span>
                <span className="text-zinc-500">Statut <span className="text-[#CCFF00]">{a.warmup_status}</span></span>
              </div>
              {a.notes && <p className="text-xs text-zinc-400 mt-3 leading-relaxed border-l-2 border-[#1E1E24] pl-3 italic">{a.notes}</p>}
            </div>
          );
        })}
      </div>

      <div className="border border-[#F59E0B]/30 bg-[#F59E0B]/5 rounded-sm p-5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="text-sm text-zinc-300">
          <span className="text-[#F59E0B] font-medium">Règle clé :</span> ne jamais dépasser le quota d'un compte sans 14j minimum de comportement stable. Compte principal ≠ compte spam — ne mélange pas les usages.
        </div>
      </div>
    </div>
  );
}

const Input = ({ label, value, onChange, type = "text", required, testid }) => (
  <label className="block">
    <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 block mb-1">{label}</span>
    <input type={type} value={value} required={required} data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#CCFF00] rounded-sm px-3 py-2 text-sm text-white outline-none" />
  </label>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 block mb-1">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#CCFF00] rounded-sm px-3 py-2 text-sm text-white outline-none">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="font-mono text-base text-white mt-0.5">{value}</div>
  </div>
);

const Quota = ({ label, value }) => (
  <div className="text-center">
    <div className="text-zinc-500 uppercase">{label}</div>
    <div className="text-white mt-0.5">{value}</div>
  </div>
);
